import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Tooltip } from "react-tooltip";

import { PAGES_DOMAIN_URL } from "constants";
import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import { numberWithCommas } from "modules/functions";

import ButtonPrimary from "components/buttons/ButtonPrimary";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";
import RollingBox from "components/other/RollingBox";
import ButtonPrimarySmall from "components/buttons/ButtonPrimarySmall";
import Dropdown from "components/forms/Dropdown";
import DropdownSelection from "components/forms/DropdownSelection";
import Tip from "components/other/Tip";
import SelectionItem from "components/forms/SelectionItem";
import InputWithLabel from "components/forms/InputWithLabel";
import InputText from "components/forms/InputText";
import Backdrop from "components/other/Backdrop";
import ModalWindow from "components/other/ModalWindow";
import MessageWindow from "components/other/MessageWindow";

import pencilIcon from "assets/img/icons/pencil-icon.svg";
import pencilWhiteIcon from "assets/img/icons/pencil-icon-white.svg";
import copyIcon from "assets/img/icons/copy-icon.svg";
import copyWhiteIcon from "assets/img/icons/copy-icon-white.svg";
import binIcon from "assets/img/icons/bin-icon.svg";
import binWhiteIcon from "assets/img/icons/bin-icon-white.svg";
import adPlanIcon from "assets/img/icons/icons8-web-advertising.svg";
import checkIcon from "assets/img/icons/icons8-check.svg";
import checkWhiteIcon from "assets/img/icons/icons8-check-white.svg";
import rightUpArrowIcon from "assets/img/icons/icons8-right-up-arrow.svg";
import rightUpArrowWhiteIcon from "assets/img/icons/icons8-right-up-arrow-white.svg";
import eyeIcon from "assets/img/icons/eye-icon.svg";
import userSingleIcon from "assets/img/icons/user-single-icon.svg";
import userSingleWhiteIcon from "assets/img/icons/user-single-icon-white.svg";
import percentWhiteIcon from "assets/img/icons/percent-icon-white.svg";
import errorIcon from "assets/img/icons/icons8-error.svg";
import listIcon from "assets/img/icons/list-icon.svg";
import listWhiteIcon from "assets/img/icons/list-icon-white.svg";

const PageListPage = styled.div`
  position: relative;

  display: flex;
`;

const PageBox = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;
  max-width: 750px;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  transition: 0.2s;

  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const PageBoxTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};

  @media (max-width: ${globalConstants.switchMobile}) {
    gap: ${css.VERTICAL_BOX_MARGIN};
  }
`;

const BoxHeadWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const BoxHeader = styled.div`
  width: 100%;

  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 30px;
`;

const PageNameWrap = styled.div`
  padding-top: 4px;
  padding-bottom: 7px;

  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: ${globalConstants.switchMobile}) {
    padding-top: 0px;
  }
`;

const PageName = styled.div`
  font-size: ${css.BOX_TITLE_FONT_SIZE};
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};
  overflow-wrap: anywhere;
`;

const PageNameEditIcon = styled.div`
  width: 14px;
  min-width: 14px;
  height: 14px;

  transform: translateY(-2px);

  &:hover {
    cursor: pointer;
  }
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const StatusBox = styled.div`
  padding-top: 6px;

  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;

  @media (max-width: ${globalConstants.switchMobile}) {
    padding-top: 2px;
  }
`;

const ControlButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ControlsLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ThumbnailLine = styled.div`
  width: 100%;

  display: flex;
  flex-direction: row;
  gap: ${css.VERTICAL_BOX_MARGIN};
  align-self: flex-start;

  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const PagePreview = styled.div`
  position: relative;

  width: calc(1440px * 0.2);
  height: calc(900px * 0.2);
  display: inline-block;
  overflow: hidden;
  position: relative;

  border-radius: ${css.BOX_RADIUS_SECONDARY};
`;

const PageThumbnail = styled.div`
  width: 1440px;
  height: 900px;
  position: relative;
  -ms-zoom: 0.2;
  -moz-transform: scale(0.2);
  -moz-transform-origin: 0 0;
  -o-transform: scale(0.2);
  -o-transform-origin: 0 0;
  -webkit-transform: scale(0.2);
  -webkit-transform-origin: 0 0;

  &:after {
    content: "";
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`;

const ThumbnailIframe = styled.iframe`
  width: 1440px;
  height: 900px;
`;

const OpenIconWrap = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 10;
`;

const PageSettings = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const SettinsgItemTitle = styled.div`
  width: 100%;
  padding-left: 3px;

  font-size: 16px;
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};

  display: flex;
  align-items: center;
  gap: 8px;
`;

const SettingsItemLine = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${css.VERTICAL_BOX_MARGIN};

  @media (max-width: ${globalConstants.switchMobile}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SettingsItemValue = styled.div`
  padding: 4px 0 4px 3px;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const SettingsItemButtonWrap = styled.div``;

const MailingListNameWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MailingListWarningWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MailingListWarningIcon = styled.div`
  width: 24px;
  min-width: 24px;

  transform: translateY(1px);
`;

const EditListButtonWrap = styled.div``;

const ListHidingBox = styled.div`
  margin-top: 10px;
  padding-bottom: 15px;
  /* min-height: 165px; */

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const DropdownWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 290px;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const ListSelectionItem = styled.div`
  font-family: "Arial", sans-serif;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const MailingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ButtonWrap = styled.div`
  display: flex;
`;

const Text = styled.div`
  line-height: 130%;
`;

const ManageLists = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const LinkFormHidingBox = styled.div`
  margin-top: 10px;
  padding-bottom: 15px;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const Warning = styled.div`
  margin-top: 7px;

  font-size: 13px;
  font-weight: 500;
  color: ${css.ERROR_RED};
`;

const ModalWrap = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1000;
`;

const DisabledModalWrap = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 100%;
  max-width: 550px;
  padding: 0 10px;
  box-sizing: border-box;
  z-index: 2000;
`;

const DisabledWindowContent = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const DisabledHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DisabledHeadingIcon = styled.div`
  width: 36px;
  min-width: 36px;
  height: 36px;
`;

const DisabledHeadingText = styled.div`
  font-size: 24px;
`;

const DisabledText = styled.div`
  text-align: center;
  line-height: 130%;
`;

const UrlInputWrap = styled.div`
  position: relative;
  max-width: 400px;
`;

const CopyIconBox = styled.div`
  position: absolute;
  top: 50%;
  right: 15px;

  transform: translateY(-50%);
`;

const StatsBoxWrap = styled.div``;

const StatsBox = styled.div`
  padding: ${css.BOX_PADDING_SECONDARY};
  max-width: 280px;
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: grid;
  grid-template-columns: 32px 73px 73px 1fr;
  grid-template-rows: 26px 26px 26px 26px 26px;
`;

const StatsLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
`;

const LabelIconCell = styled.div`
  display: flex;
  align-items: center;
`;

const LabelIcon = styled.div`
  width: 16px;
  min-width: 16px;
  height: 16px;
`;

const ValueCell = styled.div`
  height: 26px;

  font-size: 13px;

  display: flex;
  align-items: center;
`;

const ChangeBadge = styled.div`
  height: 16px;
  padding: 0 4px 1px;
  margin-left: 5px;

  font-size: 11px;

  border-radius: 8px;
  background: ${(props) =>
    props.green ? css.POSITIVE_GREEN : css.NEGATIVE_RED};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const StatusBadgeWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusBadge = styled.div`
  height: 20px;
  padding: 0 14px 1px;

  font-size: 13px;
  color: "#fff";
  white-space: nowrap;

  border-radius: 10px;
  background: ${(props) => {
    switch (props.status) {
      case "active":
        return css.BRAND_GREEN;
      case "inactive":
        return css.NEGATIVE_RED;
      default:
        return css.TEXT_GREY;
    }
  }};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const OpenListIcon = styled.div``;

const LabelIconWithTip = ({ icon, tip }) => {
  return (
    <LabelIcon>
      <img
        src={icon}
        style={{ width: "100%" }}
        data-tooltip-id={`${icon}-tip`}
        data-tooltip-content={tip}
        data-tooltip-place="top"
        data-tooltip-delay-show={500}
      />
      <Tooltip id={`${icon}-tip`} className="tooltip" />
    </LabelIcon>
  );
};

const ControlsComponent = ({
  pageId,
  pageCode,
  onEditClick,
  onDeleteClick,
  loadingDelete,
  onDuplicateClick,
}) => {
  return (
    <ControlButtons>
      <ButtonRoundIcon
        icon={pencilIcon}
        hoverIcon={pencilWhiteIcon}
        background={css.BRAND_YELLOW}
        hoverBackground={css.BRAND_GREEN}
        onClick={() => onEditClick(pageCode)}
        buttonSize="34px"
        iconSize="15px"
      />
      <ButtonRoundIcon
        icon={copyIcon}
        hoverIcon={copyWhiteIcon}
        background={css.BRAND_YELLOW}
        hoverBackground={css.BRAND_GREEN}
        onClick={() => onDuplicateClick(pageId)}
        buttonSize="34px"
        iconSize="15px"
      />
      <ButtonRoundIcon
        icon={binIcon}
        hoverIcon={binWhiteIcon}
        background={css.BRAND_YELLOW}
        hoverBackground={css.NEGATIVE_RED}
        onClick={() => onDeleteClick(pageId)}
        buttonSize="34px"
        iconSize="15px"
        disabled={loadingDelete}
      />
    </ControlButtons>
  );
};

export default ({
  id,
  code,
  list,
  stats,
  views,
  leads,
  redirectLink,
  pageLists,
  onEditClick,
  changeListToggle,
  listFormOpened,
  selectedList,
  onDeleteClick,
  loadingDelete,
  onDuplicateClick,
  listSelectionOpened,
  onListSelect,
  changeList,
  toggleListSelection,
  changeListLoading,
  changeError,
  onCloseErrorModal,
  changeLinkToggle,
  linkFormOpened,
  redirectLinkInput,
  handleLinkInputChange,
  warnings,
  changeLink,
  changeLinkLoading,
  disabledModal,
  onCloseDisabledModal,
  onOpenClick,
  editingName,
  pageName,
  handleNameInputChange,
  pageNameWarning,
  toggleNameEdit,
  updateName,
  listReady,
  listPage,
}) => {
  const listDropdownRef = useRef();
  const listSelectionRef = useRef();
  const listFormButtonRef = useRef();
  const linkDropdownRef = useRef();
  const linkSelectionRef = useRef();
  const linkFormButtonRef = useRef();
  const navigate = useNavigate();

  const [copied, setCopied] = useState(false);

  const mobileScreen = useMediaQuery({
    query: `(max-width: ${globalConstants.switchMobile})`,
  });

  function useListSelectionOutsideClick(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (
          ref.current &&
          !ref.current.contains(event.target) &&
          !listDropdownRef.current.contains(event.target) &&
          !listFormButtonRef.current.contains(event.target)
        ) {
          toggleListSelection();
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  useListSelectionOutsideClick(listSelectionRef);

  let listOptionsList = [];

  Object.keys(pageLists).forEach((list) => {
    listOptionsList.push(
      <SelectionItem
        onClick={() => onListSelect(pageLists[list])}
        key={pageLists[list].id}
      >
        <ListSelectionItem>{pageLists[list].name}</ListSelectionItem>
      </SelectionItem>
    );
  });

  const linkCopied = () => {
    setCopied(true);

    setTimeout(() => setCopied(false), 2000);
  };

  const reduceValue = (value) => {
    if (value < 10000) return numberWithCommas(value);
    return Math.round(value / 1000, 0) + "k";
  };

  const changeBadge = (change) => {
    if (typeof change !== "number") return <></>;

    if (change > 100) return <ChangeBadge green={1}>&gt;100%</ChangeBadge>;

    const roundCoef = change >= 10 || change <= -10 ? 1 : 10;
    let changeRounded = Math.round(change * roundCoef) / roundCoef;

    if (changeRounded >= 0)
      return <ChangeBadge green={1}>+{changeRounded}%</ChangeBadge>;
    else return <ChangeBadge green={0}>{changeRounded}%</ChangeBadge>;
  };

  // prepare stats
  const statsTo = stats[0] ? stats[0].to : null;

  const dayLimit = new Date();
  dayLimit.setDate(dayLimit.getDate() - 1);

  const twoDaysLimit = new Date();
  twoDaysLimit.setDate(twoDaysLimit.getDate() - 2);

  const weekLimit = new Date();
  weekLimit.setDate(weekLimit.getDate() - 7);

  const last24hoursViews = views.filter(
    (view) => view.created_at >= dayLimit.toISOString()
  );
  //console.log("last24hoursViews", id, last24hoursViews);

  const previous24hoursViews = views.filter(
    (view) =>
      view.created_at < dayLimit.toISOString() &&
      view.created_at >= twoDaysLimit.toISOString()
  );
  //console.log("previous24hoursViews", id, previous24hoursViews);

  const last24hoursViewsChange =
    previous24hoursViews.length > 0
      ? (100 / previous24hoursViews.length) * last24hoursViews.length - 100
      : "-";
  //console.log("last24hoursViewsChange", id, last24hoursViewsChange);

  const lastWeekViews = views.filter(
    (view) => view.created_at >= weekLimit.toISOString()
  );
  //console.log("lastWeekViews", id, lastWeekViews);

  const viewsFromLastTotalUpdate = statsTo
    ? views.filter((view) => view.created_at > statsTo)
    : views;

  //console.log("viewsFromLastTotalUpdate", id, viewsFromLastTotalUpdate);

  const statsTotalViews = stats[0] ? stats[0].total_views : null;

  const totalViews = statsTotalViews + viewsFromLastTotalUpdate.length;

  //console.log("totalViews", id, totalViews);

  const last24hoursLeads = leads.filter(
    (lead) => lead.created_at >= dayLimit.toISOString()
  );
  //console.log("last24hoursLeads", id, last24hoursLeads);

  const previous24hoursLeads = leads.filter(
    (lead) =>
      lead.created_at < dayLimit.toISOString() &&
      lead.created_at >= twoDaysLimit.toISOString()
  );
  //console.log("previous24hoursLeads", id, previous24hoursLeads);

  const last24hoursLeadsChange =
    previous24hoursLeads.length > 0
      ? (100 / previous24hoursLeads.length) * last24hoursLeads.length - 100
      : "-";
  //console.log("last24hoursLeadsChange", id, last24hoursLeadsChange);

  const lastWeekLeads = leads.filter(
    (lead) => lead.created_at >= weekLimit.toISOString()
  );
  //console.log("lastWeekLeads", id, lastWeekLeads);

  const leadsFromLastTotalUpdate = statsTo
    ? leads.filter((lead) => lead.created_at > statsTo)
    : leads;

  //console.log("leadsFromLastTotalUpdate", id, leadsFromLastTotalUpdate);

  const statsTotalLeads = stats[0] ? stats[0].total_leads : null;

  const totalLeads = statsTotalLeads + leadsFromLastTotalUpdate.length;

  //console.log("totalLeads", id, totalLeads);

  const verifiedLeads = leads.filter((lead) => lead.verified);

  const last24hoursVerifiedLeads = verifiedLeads.filter(
    (lead) => lead.created_at >= dayLimit.toISOString()
  );
  //console.log("last24hoursLeads", id, last24hoursLeads);

  const previous24hoursVerifiedLeads = verifiedLeads.filter(
    (lead) =>
      lead.created_at < dayLimit.toISOString() &&
      lead.created_at >= twoDaysLimit.toISOString()
  );
  //console.log("previous24hoursLeads", id, previous24hoursLeads);

  const last24hoursVerifiedLeadsChange =
    previous24hoursVerifiedLeads.length > 0
      ? (100 / previous24hoursVerifiedLeads.length) *
          last24hoursVerifiedLeads.length -
        100
      : "-";
  //console.log("last24hoursLeadsChange", id, last24hoursLeadsChange);

  const lastWeekVerifiedLeads = verifiedLeads.filter(
    (lead) => lead.created_at >= weekLimit.toISOString()
  );
  //console.log("lastWeekLeads", id, lastWeekLeads);

  const verifiedLeadsFromLastTotalUpdate = statsTo
    ? verifiedLeads.filter((lead) => lead.created_at > statsTo)
    : verifiedLeads;

  //console.log("leadsFromLastTotalUpdate", id, leadsFromLastTotalUpdate);

  const statsTotalVerifiedLeads = stats[0]
    ? stats[0].total_leads_verified
    : null;

  const totalVerifiedLeads =
    statsTotalVerifiedLeads + verifiedLeadsFromLastTotalUpdate.length;

  //console.log("totalLeads", id, totalLeads);

  const last24hoursVerifiedPercentage =
    last24hoursLeads.length > 0
      ? Math.round(
          (100 / last24hoursLeads.length) * last24hoursVerifiedLeads.length * 10
        ) / 10
      : 0;

  const lastWeekVerifiedPercentage =
    lastWeekLeads.length > 0
      ? Math.round(
          (100 / lastWeekLeads.length) * lastWeekVerifiedLeads.length * 10
        ) / 10
      : 0;

  const totalVerifiedPercentage =
    totalLeads > 0
      ? Math.round((100 / totalLeads) * totalVerifiedLeads * 10) / 10
      : 0;
  //console.log({ totalLeads, totalVerifiedLeads, totalVerifiedPercentage });

  return (
    <PageListPage>
      <PageBox key={id}>
        <PageBoxTop>
          <BoxHeadWrapper>
            <BoxHeader>
              {!editingName && (
                <PageNameWrap>
                  <PageName>{pageName}</PageName>
                  <PageNameEditIcon onClick={toggleNameEdit}>
                    <img src={pencilWhiteIcon} style={{ width: "100%" }} />
                  </PageNameEditIcon>
                </PageNameWrap>
              )}
              {editingName && (
                <PageNameWrap>
                  <InputText
                    type="text"
                    name="name"
                    value={pageName}
                    onChange={handleNameInputChange}
                    maxLength="40"
                    warning={pageNameWarning}
                    width="200px"
                  />
                  <ButtonPrimarySmall onClick={updateName}>
                    Save
                  </ButtonPrimarySmall>
                </PageNameWrap>
              )}
              <HeaderControls>
                {!editingName && (
                  <StatusBox>
                    {!listReady && (
                      <StatusBadgeWrap>
                        <StatusBadge status="inactive">Inactive</StatusBadge>
                        <div style={{ paddingBottom: "1px" }}>
                          <Tip
                            tipId={`inactive-${id}`}
                            tip="The mailing list connected to this page is not fully set up. Click the Edit List button below to complete the setup."
                            position="bottom"
                          />
                        </div>
                      </StatusBadgeWrap>
                    )}
                  </StatusBox>
                )}
                {!mobileScreen && (
                  <ControlsComponent
                    pageId={id}
                    pageCode={code}
                    onEditClick={onEditClick}
                    onDeleteClick={onDeleteClick}
                    loadingDelete={loadingDelete}
                    onDuplicateClick={onDuplicateClick}
                  />
                )}
              </HeaderControls>
            </BoxHeader>
          </BoxHeadWrapper>
          <ThumbnailLine>
            <PagePreview>
              <OpenIconWrap>
                <ButtonRoundIcon
                  icon={rightUpArrowIcon}
                  hoverIcon={rightUpArrowWhiteIcon}
                  background={css.BRAND_YELLOW}
                  hoverBackground={css.BRAND_GREEN}
                  onClick={() => onOpenClick(code)}
                  buttonSize="26px"
                  iconSize="12px"
                />
              </OpenIconWrap>
              <PageThumbnail>
                <ThumbnailIframe
                  src={`${PAGES_DOMAIN_URL}/?code=${code}&preview=true`}
                  frameBorder="0"
                ></ThumbnailIframe>
              </PageThumbnail>
            </PagePreview>
            <StatsBoxWrap>
              <StatsBox>
                <StatsLabel></StatsLabel>
                <StatsLabel>24 Hours</StatsLabel>
                <StatsLabel>7 Days</StatsLabel>
                <StatsLabel>Total</StatsLabel>
                <LabelIconCell>
                  <LabelIconWithTip icon={eyeIcon} tip="Views" />
                </LabelIconCell>
                <ValueCell>
                  {numberWithCommas(last24hoursViews.length)}
                  {changeBadge(last24hoursViewsChange)}
                </ValueCell>
                <ValueCell>{numberWithCommas(lastWeekViews.length)}</ValueCell>
                <ValueCell>{reduceValue(totalViews)}</ValueCell>
                <LabelIconCell>
                  <LabelIconWithTip icon={userSingleIcon} tip="Leads" />
                </LabelIconCell>
                <ValueCell>
                  {numberWithCommas(last24hoursLeads.length)}
                  {changeBadge(last24hoursLeadsChange)}
                </ValueCell>
                <ValueCell>{numberWithCommas(lastWeekLeads.length)}</ValueCell>
                <ValueCell>{reduceValue(totalLeads)}</ValueCell>
                <LabelIconCell>
                  <LabelIconWithTip icon={userSingleWhiteIcon} tip="Leads" />
                </LabelIconCell>
                <ValueCell>
                  {numberWithCommas(last24hoursVerifiedLeads.length)}
                  {changeBadge(last24hoursVerifiedLeadsChange)}
                </ValueCell>
                <ValueCell>
                  {numberWithCommas(lastWeekVerifiedLeads.length)}
                </ValueCell>
                <ValueCell>{reduceValue(totalVerifiedLeads)}</ValueCell>
                <LabelIconCell>
                  <LabelIconCell>
                    <LabelIconWithTip
                      icon={percentWhiteIcon}
                      tip="The percentage of verified leads out of the total number of collected contacts."
                    />
                  </LabelIconCell>
                </LabelIconCell>
                <ValueCell>{last24hoursVerifiedPercentage}%</ValueCell>
                <ValueCell>{lastWeekVerifiedPercentage}%</ValueCell>
                <ValueCell>{totalVerifiedPercentage}%</ValueCell>
              </StatsBox>
            </StatsBoxWrap>
          </ThumbnailLine>
        </PageBoxTop>

        <InputWithLabel
          label="Landing Page URL"
          tip="The web address of your landing page. Copy and share this address with your audience.<br/><br/><a href='https://help.scalelup.com/en/articles/11884029-how-to-share-your-landing-page-and-collect-leads' target='_blank' class='tooltip-link'>Learn more</a>"
          tipId={`pageUrl-${id}`}
          clickableTip
        >
          <UrlInputWrap>
            <InputText
              type="text"
              value={`${PAGES_DOMAIN_URL}/?code=${code}`}
              readOnly={true}
              righticonspace={true}
            />
            <CopyIconBox>
              <CopyToClipboard
                text={`${PAGES_DOMAIN_URL}/?code=${code}`}
                onCopy={linkCopied}
              >
                <ButtonRoundIcon
                  icon={copied ? checkIcon : copyIcon}
                  hoverIcon={copied ? checkWhiteIcon : copyWhiteIcon}
                  background={css.BRAND_YELLOW}
                  hoverBackground={css.BRAND_GREEN}
                  buttonSize="24px"
                  iconSize="10px"
                />
              </CopyToClipboard>
            </CopyIconBox>
          </UrlInputWrap>
        </InputWithLabel>

        <PageSettings>
          <RollingBox>
            <MailingList>
              <SettinsgItemTitle>
                Mailing List
                <Tip
                  tipId={`mailingList-${id}`}
                  tip="The list where collected leads will be saved.<br/><br/><a href='https://help.scalelup.com/en/articles/11884085-connecting-and-configuring-mailing-lists-for-landing-pages' target='_blank' class='tooltip-link'>Learn more</a>"
                  clickable
                />
              </SettinsgItemTitle>
              <SettingsItemLine>
                <SettingsItemValue>
                  <MailingListNameWrap>
                    {list.name}
                    {!listReady && (
                      <MailingListWarningWrap>
                        <MailingListWarningIcon>
                          <img src={errorIcon} style={{ width: "100%" }} />
                        </MailingListWarningIcon>
                        <EditListButtonWrap>
                          <ButtonPrimarySmall
                            onClick={() => {
                              navigate(`/create-mailing-list?id=${list.id}`);
                            }}
                          >
                            Edit List
                          </ButtonPrimarySmall>
                        </EditListButtonWrap>
                      </MailingListWarningWrap>
                    )}
                  </MailingListNameWrap>
                  <OpenListIcon>
                    <ButtonRoundIcon
                      icon={listIcon}
                      hoverIcon={listWhiteIcon}
                      background={css.BRAND_YELLOW}
                      hoverBackground={css.BRAND_GREEN}
                      buttonSize="24px"
                      iconSize="10px"
                      onClick={() =>
                        navigate(
                          `/mailing-list?id=${list.id}&from=landing-pages&page=${listPage}`
                        )
                      }
                    />
                  </OpenListIcon>
                </SettingsItemValue>
                <SettingsItemButtonWrap ref={listFormButtonRef}>
                  <ButtonPrimarySmall onClick={changeListToggle}>
                    {listFormOpened ? "Close" : "Change List"}
                  </ButtonPrimarySmall>
                </SettingsItemButtonWrap>
              </SettingsItemLine>
              {listFormOpened && (
                <ListHidingBox>
                  <Text>
                    Select the mailing list where leads collected from this page
                    will be saved.
                  </Text>
                  <DropdownWrap>
                    <Dropdown
                      onClick={toggleListSelection}
                      dropdownRef={listDropdownRef}
                    >
                      <ListSelectionItem>{selectedList.name}</ListSelectionItem>
                    </Dropdown>
                    {listSelectionOpened && (
                      <DropdownSelection
                        width="290px"
                        top="48px"
                        selectionRef={listSelectionRef}
                        maxHeight="220px"
                      >
                        {listOptionsList}
                      </DropdownSelection>
                    )}
                    <ButtonWrap>
                      <ButtonPrimary
                        onClick={changeList}
                        disabled={changeListLoading}
                      >
                        Save
                      </ButtonPrimary>
                    </ButtonWrap>
                  </DropdownWrap>
                  <ManageLists>
                    <Text>
                      You can create a new mailing list by clicking the Manage
                      Lists button.
                    </Text>
                    <ButtonWrap>
                      <ButtonPrimarySmall
                        onClick={() => navigate("/mailing-lists")}
                      >
                        Manage Lists
                      </ButtonPrimarySmall>
                    </ButtonWrap>
                  </ManageLists>
                </ListHidingBox>
              )}
            </MailingList>
          </RollingBox>
          <RollingBox>
            <MailingList>
              <SettinsgItemTitle>
                Redirect Link
                <Tip
                  tipId={`redirectUrl-${id}`}
                  tip={`The web address to which visitors of your landing page are redirected after they fill out and submit the contact form.<br><br>
                  Enter a valid URL that your leads will visit after submitting the form.<br/><br/><a href='https://help.scalelup.com/en/articles/11884495-redirect-links-how-to-set-and-change-the-destination-url' target='_blank' class='tooltip-link'>Learn more</a>`}
                  clickable
                />
              </SettinsgItemTitle>
              <SettingsItemLine>
                <SettingsItemValue>{redirectLink}</SettingsItemValue>
                <SettingsItemButtonWrap ref={linkFormButtonRef}>
                  <ButtonPrimarySmall onClick={changeLinkToggle}>
                    {linkFormOpened ? "Close" : "Change Link"}
                  </ButtonPrimarySmall>
                </SettingsItemButtonWrap>
              </SettingsItemLine>
              {linkFormOpened && (
                <LinkFormHidingBox>
                  <InputWithLabel label="Change Redirect URL">
                    <InputText
                      type="text"
                      name="redirectUrl"
                      value={redirectLinkInput}
                      placeholder="Enter your website URL link"
                      onChange={handleLinkInputChange}
                      maxLength={500}
                      warning={warnings.includes("redirectUrl")}
                    />
                    {warnings.includes("redirectUrl") && (
                      <Warning>
                        The entered URL is invalid. The address should start
                        with https://
                      </Warning>
                    )}
                  </InputWithLabel>

                  <ButtonWrap>
                    <ButtonPrimary
                      onClick={changeLink}
                      disabled={changeLinkLoading}
                    >
                      Save
                    </ButtonPrimary>
                  </ButtonWrap>
                </LinkFormHidingBox>
              )}
            </MailingList>
          </RollingBox>
        </PageSettings>
        {mobileScreen && (
          <ControlsLine>
            <ControlsComponent
              pageId={id}
              pageCode={code}
              onEditClick={onEditClick}
              onDeleteClick={onDeleteClick}
              loadingDelete={loadingDelete}
              onDuplicateClick={onDuplicateClick}
            />
          </ControlsLine>
        )}
      </PageBox>
      {disabledModal && (
        <ModalWrap>
          <Backdrop
            onClick={onCloseDisabledModal}
            background="rgba(0, 0, 0, 0.7)"
          />
          <DisabledModalWrap>
            <ModalWindow
              modal="changeError"
              handleModalClose={onCloseDisabledModal}
              fullScreenHeight={true}
            >
              <DisabledWindowContent>
                <DisabledHeading>
                  <DisabledHeadingIcon>
                    <img
                      src={adPlanIcon}
                      style={{ width: "100%", paddingTop: "1px" }}
                    />
                  </DisabledHeadingIcon>
                  <DisabledHeadingText>
                    Subscribe to Ad Plan
                  </DisabledHeadingText>
                </DisabledHeading>
                <DisabledText>
                  By default, your landing pages redirect visitors who submit
                  the form to the Scalelup website using your referral link. If
                  you want to use a different link and redirect your leads
                  elsewhere, you need to have an active Scalelup Ad Plan.
                </DisabledText>
                <ButtonWrap>
                  <ButtonPrimary
                    onClick={() => navigate("/advertising-plans")}
                    disabled={changeLinkLoading}
                  >
                    Get Ad Plan
                  </ButtonPrimary>
                </ButtonWrap>
              </DisabledWindowContent>
            </ModalWindow>
          </DisabledModalWrap>
        </ModalWrap>
      )}
      {changeError && (
        <ModalWrap>
          <Backdrop
            onClick={onCloseErrorModal}
            background="rgba(0, 0, 0, 0.7)"
          />
          <MessageWindow handleModalClose={onCloseErrorModal} title="Error">
            {changeError}
          </MessageWindow>
        </ModalWrap>
      )}
    </PageListPage>
  );
};
